import { BiddingConsoleImageSlider } from './components/BiddingConsoleImageSlider/BiddingConsoleImageSlider';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { CurrentItemDetails } from './components/CurrentItemDetails/CurrentItemDetails';
import { Fire } from '@liveauctioneers/hammer-ui-core/icons';
import { getItemSummaryLotNumber, getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getSavedItemCountNum } from '@/redux/modules/savedItemCount';
import { H2 } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { POPULAR_ITEM_MINIMUM_SAVE_COUNT } from '@/components/ItemSave/ItemSave.constants';
import { useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import ItemCardSaveIcon from '@/components/GridItemCard/Card/components/ItemCardSaveIcon/ItemCardSaveIcon';

type Props = {
    itemId: number;
};

const SCROLL_THRESHOLD = 32;

export const ItemDetails = ({ itemId }: Props) => {
    const { formatMessage } = useIntl();
    const liveItemTitle = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const numberOfWatchers = useAppSelector((state) => getSavedItemCountNum(state, itemId));
    const liveItemLotNumber = useAppSelector((state) => getItemSummaryLotNumber(state, itemId));
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);

    const handleScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }

        const { clientHeight, scrollHeight, scrollTop } = container;
        const isScrollable = scrollHeight > clientHeight;
        const isAtTop = scrollTop <= SCROLL_THRESHOLD;

        setShowTooltip(isScrollable && isAtTop);
    }, []);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }

        container.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    return (
        <div
            className="relative flex flex-col gap-sm overflow-y-auto bg-background-primary px-md py-sm"
            ref={scrollContainerRef}
        >
            <div>
                <div className="flex items-center justify-between gap-sm">
                    <H2>
                        {formatMessage(
                            { id: 'bidding_console.console.itemTitle' },
                            { lotNumber: liveItemLotNumber, title: liveItemTitle }
                        )}
                    </H2>
                    <ItemCardSaveIcon
                        itemId={itemId}
                        labeled
                    />
                </div>
                {numberOfWatchers > 0 && (
                    <div className="mt-2xs flex items-center gap-2xs">
                        {numberOfWatchers >= POPULAR_ITEM_MINIMUM_SAVE_COUNT && (
                            <Fire
                                color="critical"
                                size="sm"
                                style="fill"
                            />
                        )}

                        <BodySecondary>
                            {formatMessage({ id: 'bidding_console.console.watchers' }, { count: numberOfWatchers })}
                        </BodySecondary>
                    </div>
                )}
            </div>
            <BiddingConsoleImageSlider itemId={itemId} />
            <CurrentItemDetails itemId={itemId} />
            {showTooltip && (
                <div className="sticky -bottom-sm z-10 flex justify-center py-xs">
                    <div className="w-[203px] rounded-md bg-background-primary px-sm py-xs shadow-02">
                        <BodySecondary className="text-text-primary">
                            {formatMessage({ id: 'bidding_console.console.scrollForMoreDetails' })}
                        </BodySecondary>
                    </div>
                </div>
            )}
        </div>
    );
};
